import styled, { css } from "styled-components";
import { Dropdown as BsDropdown } from "react-bootstrap";
import { ChevronDown, Check } from "lucide-react";

// $block = full-width mode: root stretches so toggle + menu fill the parent.
export const StyledDropdown = styled(BsDropdown)`
  ${({ $block }) => $block && "width: 100%;"}
`;

export const Chevron = styled(ChevronDown)`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  stroke: #a4a7ae;
  stroke-width: 1.667px;
  transition: transform 0.2s ease;
`;

export const CheckIcon = styled(Check).attrs({ size: 18 })`
  flex-shrink: 0;
`;

export const StyledToggle = styled(BsDropdown.Toggle)`
  display: ${({ $block }) => ($block ? "flex" : "inline-flex")};
  width: ${({ $block }) => ($block ? "100%" : "auto")};
  justify-content: ${({ $block }) => ($block ? "flex-start" : "center")};
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.09);
  background: #fff;
  font-family: Geist, sans-serif;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  box-shadow: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  color: rgba(10, 13, 18, 0.7);
  font-weight: 500;

  // Kill react-bootstrap's default caret so our Chevron owns the visual.
  &::after {
    display: none;
  }

  // Override bootstrap's .btn-light hover/focus/active.
  &&:hover,
  &&:focus,
  &&:active {
    background: #fff;
    border-color: rgba(0, 0, 0, 0.15);
    box-shadow: none;
  }

  &&.show {
    background: #fff;
    border-color: ${({ theme }) => theme.button?.bg?.primary || "#2258F1"};
    box-shadow: 0 0 0 1px
      ${({ theme }) => theme.button?.bg?.primary || "#2258F1"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  // Flip the chevron when the menu is open.
  &.show ${Chevron} {
    transform: rotate(180deg);
  }
`;

export const TriggerIconWrap = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

export const TriggerLabel = styled.span`
  flex: 1 0 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledMenu = styled(BsDropdown.Menu)`
  margin-top: 4px;
  padding: 4px;
  min-width: ${({ $block, $minWidth }) =>
    $block ? "100%" : $minWidth || "240px"};
  max-width: 360px;
  width: max-content;
  background: ${({ theme }) => theme.dropdown?.bg?.primary || "#fff"};
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 12px;
  box-shadow: 0 12px 16px -4px rgba(10, 13, 18, 0.08),
    0 4px 6px -2px rgba(10, 13, 18, 0.03), 0 2px 2px -1px rgba(10, 13, 18, 0.04);
  overflow: hidden;

  &.show {
    display: flex;
    flex-direction: column;
  }
`;

// ScrollArea has its own scroll; the footer below stays pinned.
export const ScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 320px;
  overflow-y: auto;
`;

export const OptionIconWrap = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

export const OptionLabelColumn = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
`;

export const OptionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OptionDescription = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: rgba(10, 13, 18, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CheckMark = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.button?.bg?.primary || "#2258F1"};
  display: flex;
  align-items: center;
`;

export const Row = styled.button.attrs({ type: "button" })`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-family: Geist, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: rgba(10, 13, 18, 0.9);
  cursor: pointer;
  transition: background-color 0.1s ease;
  text-align: left;

  // Defeat the global body button[disabled] border.
  &&[disabled] {
    border: 0 !important;
  }

  &:hover {
    background: rgba(10, 13, 18, 0.04);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $selected }) =>
    $selected &&
    css`
      background: rgba(10, 13, 18, 0.04);
    `}
`;

export const FooterBar = styled.div`
  flex-shrink: 0;
  padding: 6px 4px 4px;
`;

export const EmptyState = styled.div`
  padding: 16px 12px;
  text-align: center;
  font-family: Geist, sans-serif;
  font-size: 13px;
  line-height: 20px;
  color: rgba(10, 13, 18, 0.5);
`;
